import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { environment } from '../../../environments/environment';

interface RecentLink {
  url: string;
  status: string;
  dateTime: string;
}

interface DashboardUser {
  credits: number;
  totalLinks: number;
  fullName?: string;
  name?: string;
  email?: string;
  joinDate?: string;
}

interface DashboardLinksResponse {
  success: boolean;
  data: {
    links: RecentLink[];
    submitted?: number;
    pending?: number;
    rejected?: number;
    user?: DashboardUser;
  };
}

interface ApiKeyResponse {
  success?: boolean;
  apiKey?: string;
  data?: {
    apiKey?: string;
    api_key?: string;
    has_api_key?: boolean;
    masked_key?: string;
  };
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './dashboard.html',
  styleUrls: ['./dashboard.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {

  // State
  isLoading = true;
  userName = '';
  greeting = '';

  // Data
  credits = 0;
  totalLinks = 0;
  submitted = 0;
  pending = 0;
  rejected = 0;
  weeklyLinks = 0;
  recentLinks: RecentLink[] = [];
  lastUpdate = '';

  // Animated values
  animCredits = 0;
  animTotal = 0;
  animSubmitted = 0;

  // API Key
  apiKey = '';
  maskedKey = '';
  hasApiKey = false;
  apiKeyRevealed = false;
  apiKeyLoading = false;
  copySuccess = false;
  toastMsg = '';
  toastType: 'ok' | 'err' = 'ok';

  // Dock tooltip
  dockTooltip = '';
  dockTooltipX = 0;

  private timer: ReturnType<typeof setInterval> | null = null;
  private rafs: number[] = [];

  constructor(private router: Router) {}

  ngOnInit() {
    this.setGreeting();
    if (!this.checkAuth()) return;
    this.loadAll();
    this.timer = setInterval(() => this.loadAll(true), 30000);
  }

  ngOnDestroy() {
    if (this.timer) clearInterval(this.timer);
    this.rafs.forEach(id => cancelAnimationFrame(id));
  }

  private setGreeting() {
    const h = new Date().getHours();
    if (h < 12) this.greeting = 'Good morning';
    else if (h < 17) this.greeting = 'Good afternoon';
    else this.greeting = 'Good evening';
  }

  private checkAuth(): boolean {
    const token = localStorage.getItem('auth_token');
    const raw = localStorage.getItem('userData');
    if (!token || !raw) { this.router.navigate(['/login']); return false; }
    try {
      const u = JSON.parse(raw);
      this.userName = u.fullName || u.name || u.username || 'User';
      this.credits = u.credits || 0;
      this.totalLinks = u.totalLinks || 0;
      return true;
    } catch { this.router.navigate(['/login']); return false; }
  }

  loadAll(silent = false) {
    if (!silent) this.isLoading = true;
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    Promise.all([
      fetch(`${environment.apiUrl}/user/links-simple.php?limit=10`, { headers }).then(r => r.json()) as Promise<DashboardLinksResponse>,
      fetch(`${environment.apiUrl}/user/api-key.php`, { headers }).then(r => r.json()).catch(() => null) as Promise<ApiKeyResponse | null>
    ]).then(([linksData, keyData]) => {
      if (linksData?.success) {
        this.recentLinks = linksData.data.links || [];
        this.submitted = linksData.data.submitted || 0;
        this.pending = linksData.data.pending || 0;
        this.rejected = linksData.data.rejected || 0;
        if (linksData.data.user) {
          this.credits = linksData.data.user.credits;
          this.totalLinks = linksData.data.user.totalLinks;
          this.userName = linksData.data.user.fullName || linksData.data.user.name || 'User';
          localStorage.setItem('userData', JSON.stringify(linksData.data.user));
        }
        this.weeklyLinks = this.calcWeekly();
        this.animateTo('animCredits', this.credits);
        this.animateTo('animTotal', this.totalLinks);
        this.animateTo('animSubmitted', this.submitted);
      }
      if (keyData) {
        const rawKey = keyData.data?.apiKey || keyData.apiKey;
        if (rawKey) {
          this.hasApiKey = true;
          this.apiKey = rawKey;
          this.maskedKey = rawKey.substring(0, 12) + '••••••••••••';
        } else if (keyData.success && keyData.data?.has_api_key) {
          this.hasApiKey = true;
          this.maskedKey = keyData.data.masked_key || '••••••••';
        }
      }
      this.isLoading = false;
      this.lastUpdate = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }).catch((err) => {
      console.error('Dashboard load error:', err);
      this.isLoading = false;
    });
  }

  // ── Animated counters ──
  private animateTo(prop: 'animCredits' | 'animTotal' | 'animSubmitted', target: number) {
    const start = this[prop] || 0;
    const diff = target - start;
    if (diff === 0) { this[prop] = target; return; }
    const t0 = performance.now();
    const dur = 900;
    const tick = (now: number) => {
      const p = Math.min((now - t0) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      this[prop] = Math.round(start + diff * e);
      if (p < 1) this.rafs.push(requestAnimationFrame(tick));
    };
    this.rafs.push(requestAnimationFrame(tick));
  }

  // ── Computed ──
  get totalAll(): number { return this.submitted + this.pending + this.rejected; }
  get successRate(): number { return this.totalAll ? Math.round((this.submitted / this.totalAll) * 100) : 0; }
  get creditEfficiency(): number { return this.successRate || 96; } // Fallback to 96% if no links yet
  get creditsPct(): number { return Math.min((this.credits / 500) * 100, 100); }
  get successStroke(): number {
    const c = 2 * Math.PI * 58;
    return c - (this.successRate / 100) * c;
  }
  get creditsStroke(): number {
    const c = 2 * Math.PI * 58;
    return c - (this.creditsPct / 100) * c;
  }
  get circumference(): number { return 2 * Math.PI * 58; }

  // ── Weekly sparkline ──
  private calcWeekly(): number {
    const ago = new Date(); ago.setDate(ago.getDate() - 7);
    return this.recentLinks.filter(l => new Date(l.dateTime) >= ago).length;
  }

  getDailyData(): number[] {
    const counts = [0,0,0,0,0,0,0];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today); d.setDate(today.getDate() - i);
      counts[6 - i] = this.recentLinks.filter(l => new Date(l.dateTime).toDateString() === d.toDateString()).length;
    }
    return counts;
  }

  sparklinePath(): string {
    const data = this.getDailyData();
    const max = Math.max(...data, 1);
    const w = 120, h = 32;
    const pts = data.map((v, i) => `${(i / 6) * w},${h - (v / max) * (h - 4)}`);
    return `M${pts.join(' L')}`;
  }

  // ── API Key ──
  generateKey() {
    this.apiKeyLoading = true;
    const token = localStorage.getItem('auth_token') || '';
    fetch(`${environment.apiUrl}/user/api-key.php`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    })
    .then(r => r.json())
    .then(d => {
      // api-key.php POST wraps as {success:true, data:{apiKey:'...'}} 
      const key = d.data?.apiKey || d.apiKey || d.data?.api_key;
      if (key) {
        this.apiKey = key;
        this.hasApiKey = true;
        this.maskedKey = key.substring(0, 12) + '••••••••••••';
        this.apiKeyRevealed = true;
        this.toast('Key generated! Copy it now.', 'ok');
      } else { this.toast('Generation failed. Try again.', 'err'); }
      this.apiKeyLoading = false;
    })
    .catch(() => { this.toast('Network error', 'err'); this.apiKeyLoading = false; });
  }

  revokeKey() {
    this.apiKeyLoading = true;
    const token = localStorage.getItem('auth_token') || '';
    fetch(`${environment.apiUrl}/user/api-key.php`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    })
    .then(r => r.json())
    .then(() => {
      this.apiKey = ''; this.maskedKey = ''; this.hasApiKey = false;
      this.apiKeyRevealed = false;
      this.toast('Key revoked', 'ok');
      this.apiKeyLoading = false;
    })
    .catch(() => { this.toast('Revoke failed', 'err'); this.apiKeyLoading = false; });
  }

  toggleKeyReveal() { this.apiKeyRevealed = !this.apiKeyRevealed; }

  copyKey() {
    const k = this.apiKey || this.maskedKey;
    if (!k) return;
    navigator.clipboard?.writeText(k).then(() => {
      this.copySuccess = true;
      this.toast('Copied!', 'ok');
      setTimeout(() => this.copySuccess = false, 2000);
    }).catch(() => {
      const t = document.createElement('textarea');
      t.value = k; document.body.appendChild(t); t.select();
      document.execCommand('copy'); document.body.removeChild(t);
      this.copySuccess = true; setTimeout(() => this.copySuccess = false, 2000);
    });
  }

  get displayedKey(): string {
    if (!this.hasApiKey) return 'No key generated yet';
    return this.apiKeyRevealed && this.apiKey ? this.apiKey : this.maskedKey;
  }

  private toast(msg: string, type: 'ok' | 'err') {
    this.toastMsg = msg; this.toastType = type;
    setTimeout(() => this.toastMsg = '', 3500);
  }

  // ── Helpers ──
  truncUrl(url: string): string {
    if (!url) return '';
    try {
      const u = new URL(url);
      const path = u.pathname.length > 30 ? u.pathname.substring(0, 27) + '...' : u.pathname;
      return u.hostname + path;
    } catch { return url.length > 50 ? url.substring(0, 47) + '...' : url; }
  }

  timeAgo(dt: string): string {
    const d = new Date(dt);
    if (isNaN(d.getTime())) return '--';
    const ms = Date.now() - d.getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'now';
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const dd = Math.floor(h / 24);
    return dd < 7 ? `${dd}d` : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  statusColor(s: string): string {
    const l = s.toLowerCase();
    if (l === 'submitted') return 'var(--pulse)';
    if (l === 'pending' || l === 'drip scheduled') return 'var(--amber)';
    return 'var(--crimson)';
  }

  openUrl(url: string) { window.open(url, '_blank'); }
  copyUrl(url: string) { navigator.clipboard?.writeText(url); }
  trackByUrl(i: number, l: RecentLink) { return l.url; }

  showDock(label: string, ev: MouseEvent) {
    this.dockTooltip = label;
    const el = ev.target as HTMLElement;
    const rect = el.closest('.dock-item')?.getBoundingClientRect();
    if (rect) this.dockTooltipX = rect.left + rect.width / 2;
  }
  hideDock() { this.dockTooltip = ''; }


  // ── Account Info ──
  get userEmail(): string {
    try {
      const u = JSON.parse(localStorage.getItem('userData') || '{}');
      return u.email || u.Email || '';
    } catch { return ''; }
  }

  get memberSince(): string {
    try {
      const u = JSON.parse(localStorage.getItem('userData') || '{}');
      const d = new Date(u.joinDate || u.CreatedAt || u.createdAt);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } catch { return ''; }
  }
}